import { AI_BUDDIES } from '../../constants';
import agentApi from '../agent/agentApi';
import type { AIBuddy, FeedPost } from '../../types';

import { getFullImageUrl } from '../../utils/imageUrl';
import { getTimezoneOffsetMinutes } from '../../utils/dateHelpers';
import { getApiErrorMessage } from '../../utils/apiErrorHandler';

export interface SuggestCommentResult {
  success: boolean;
  message?: string;
  postCommentId?: number;
  commentText?: string;
}

export interface AiBuddySettings {
  keepHistoryFor30Days: boolean;
  enableSuggestLikes: boolean;
  enableSuggestComments: boolean;
}

export interface AiBuddySettingsPayload {
  agentUserId: number;
  keepHistoryFor30Days: boolean;
  enableSuggestLikes: boolean;
  enableSuggestComments: boolean;
}

// Simulate network delay
const delay = (ms = 500): Promise<void> =>
  new Promise(resolve => setTimeout(resolve, ms));

const getAIBuddies = async () => {
  const timezoneOffset = getTimezoneOffsetMinutes();
  const response = await agentApi.agentsList({ timezoneOffset });
  const data = response.data;
  if (data && Array.isArray(data.agents)) {
    const agents: AIBuddy[] = data.agents.map((agent: any) => ({
      id: agent.agentId.toString(),
      name: agent.userName || 'AI Buddy',
      role: agent.motivationStyle || 'AI Buddy',
      description: agent.bio || agent.tone || '',
      about: agent.bio || '',
      profileUserGuid: agent.profileUserGuid || agent.agentGuid || agent.userGuid || '',
      image: getFullImageUrl(agent.agentPhoto),
      age: 0,
      country: '',
      goalLbs: 0,
      specialties: [agent.motivationStyle, agent.tone].filter(Boolean),
      isFollowing: agent.isFollowing === true || agent.isFollowing === 1,
    }));
    return { data: agents };
  }
  return { data: [] };
};

const getBuddyById = async (id: string) => {
  await delay();
  const buddy = AI_BUDDIES.find(b => b.id === id);
  if (!buddy) {
    throw new Error('Buddy not found');
  }
  return { data: buddy };
};

/**
 * `template` carries the placeholder posts until the feed endpoint exists; it is
 * supplied by the caller because the fixtures reference bundled UI assets.
 */
const getBuddyFeed = async (buddyId: string, template: FeedPost[] = []) => {
  await delay();
  const buddy = AI_BUDDIES.find(b => b.id === buddyId);
  if (!buddy) {
    throw new Error('Buddy not found');
  }
  const feed = template.map(post => ({
    ...post,
    username: buddy.name,
    avatar: buddy.image,
    userId: buddy.id
  }));
  return { data: feed };
};

const getAiBuddySettings = async (agentUserId: number): Promise<{ data: AiBuddySettings | null }> => {
  try {
    const response = await agentApi.getAiBuddySettings(agentUserId);
    const resData = response.data?.data;
    if (!resData) {
      return { data: null };
    }
    return {
      data: {
        keepHistoryFor30Days: !!resData.keepHistoryFor30Days,
        enableSuggestLikes: !!resData.enableSuggestLikes,
        enableSuggestComments: !!resData.enableSuggestComments,
      },
    };
  } catch (err: any) {
    const message =
      err?.response?.data?.message || err?.message || 'Failed to fetch AI Settings';
    throw new Error(message);
  }
};

const saveAiBuddySettings = async (payload: AiBuddySettingsPayload): Promise<{ success: boolean }> => {
  try {
    await agentApi.saveAiBuddySettings(payload);
    return { success: true };
  } catch (err: any) {
    const message =
      err?.response?.data?.message || err?.message || 'Failed to save settings';
    throw new Error(message);
  }
};

const suggestComment = async (
  postId: number,
  agentUserId: number,
): Promise<SuggestCommentResult> => {
  try {
    const response = await agentApi.suggestComment({ postId, agentUserId });
    const data = response.data;
    const isSuccess =
      (response.status === 201 || response.status === 200) &&
      data?.success !== false &&
      typeof data?.postCommentId === 'number' &&
      typeof data?.commentText === 'string';

    if (isSuccess) {
      return {
        success: true,
        message: data.message,
        postCommentId: data.postCommentId,
        commentText: data.commentText,
      };
    }

    return {
      success: false,
      message: data?.message || 'Failed to suggest comment',
    };
  } catch (err: unknown) {
    return {
      success: false,
      message: getApiErrorMessage(err as import('../../types/api').ApiError),
    };
  }
};

const aiBuddyService = {
  getAIBuddies,
  getBuddyById,
  getBuddyFeed,
  getAiBuddySettings,
  saveAiBuddySettings,
  suggestComment,
};

export default aiBuddyService;
